import { Search, X } from 'lucide-react'
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs'
import { useCallback, useMemo } from 'react'
import useSWR from 'swr'

import { Money } from '@/components/ds'
import { Input } from '@/components/ui/input'
import { ScrollFadeArea } from '@/components/ui/scroll-fade-area'
import { Skeleton } from '@/components/ui/skeleton'
import { apiFetcher } from '@/data/fetcher'
import { Item } from '@/interfaces/cash-register/Item'
import { Category } from '@/interfaces/catalog/Category'
import { Product } from '@/interfaces/catalog/Product'
import { Sku } from '@/interfaces/catalog/Sku'
import { findCategoryById, getDescendants } from '@/lib/catalog/categoryUtils'

import { CategoryNavigation } from './CategoryNavigation'
import { ProductTile } from './ProductTile'
import { normalize, plural, skuTaxRate, skuUnitPrice } from './registerUtils'
import { SkuSelectionView } from './SkuSelectionView'
import { VariantAxisPicker } from './VariantAxisPicker'

interface ProductCatalogProps {
  items: Item[]
  onItemsChange: (items: Item[]) => void
  getAvailableToAdd: (skuId: string) => number
  getMaxQuantityForLine: (skuId: string, lineIndex: number) => number
}

function groupSkusByProductId(skus: Sku[]): Map<string, Sku[]> {
  const map = new Map<string, Sku[]>()
  for (const sku of skus) {
    const existing = map.get(sku.productId) ?? []
    existing.push(sku)
    map.set(sku.productId, existing)
  }
  return map
}

function priceDisplay(skus: Sku[]) {
  const priced = skus.filter((sku) => skuUnitPrice(sku) > 0)
  if (priced.length === 0) return null
  const currency = priced[0].price?.price?.currency ?? 'CZK'
  const min = Math.min(...priced.map(skuUnitPrice))
  const varies = priced.some((sku) => skuUnitPrice(sku) !== min)
  return (
    <span className="inline-flex items-baseline gap-1">
      {varies && <span className="text-xs text-muted-foreground">od</span>}
      <Money amount={min} minor={false} currency={currency} size="sm" />
    </span>
  )
}

/**
 * Katalog na levé straně pokladny: hledání, kategorie, mřížka produktů a výběr varianty.
 * Logika přidávání (sloučení řádků, limity skladu, produkt bez SKU) je převzatá z původního modálu.
 */
export function ProductCatalog({ items, onItemsChange, getAvailableToAdd, getMaxQuantityForLine }: ProductCatalogProps) {
  // stav procházení je v URL: Zpět v prohlížeči vrací kategorie i otevřený produkt, reload ho obnoví
  const [categoryPath, setCategoryPath] = useQueryState(
    'kategorie',
    parseAsArrayOf(parseAsString).withDefault([]).withOptions({ history: 'push' }),
  )
  const [selectedProductId, setSelectedProductId] = useQueryState('produkt', parseAsString.withOptions({ history: 'push' }))
  const [query, setQuery] = useQueryState('hledat', parseAsString.withDefault('').withOptions({ history: 'replace' }))

  const { data: categoriesResponse } = useSWR<{ data?: Category[] }>('/api/v2/categories', apiFetcher)
  const { data: productsResponse, isLoading: productsLoading } = useSWR<{ data?: Product[] }>('/api/v2/products', apiFetcher)
  const { data: skusResponse } = useSWR<{ data?: Sku[] }>('/api/v2/skus', apiFetcher)

  const categories = useMemo(() => categoriesResponse?.data ?? [], [categoriesResponse?.data])
  const products = useMemo(
    () => (productsResponse?.data ?? []).filter((p) => p.isActive !== false),
    [productsResponse?.data],
  )
  const selectedProduct = useMemo(
    () => (selectedProductId ? products.find((p) => p.id === selectedProductId) ?? null : null),
    [products, selectedProductId],
  )
  const skusByProductId = useMemo(
    () => groupSkusByProductId((skusResponse?.data ?? []).filter((s) => s.isActive !== false)),
    [skusResponse?.data],
  )

  // kategorie, ve kterých (nebo v jejichž potomcích) je aspoň jeden produkt
  const categoryHasProducts = useMemo(() => {
    const direct = new Set(products.map((p) => p.mainCategoryId).filter(Boolean) as string[])
    const result = new Map<string, boolean>()
    const visit = (category: Category): boolean => {
      const childHas = (category.children ?? []).map(visit).some(Boolean)
      const has = direct.has(category.id) || childHas
      result.set(category.id, has)
      return has
    }
    categories.forEach(visit)
    return result
  }, [categories, products])

  const isVisible = useCallback((category: Category) => categoryHasProducts.get(category.id) === true, [categoryHasProducts])

  const visibleProducts = useMemo(() => {
    const q = normalize(query.trim())
    if (q) {
      return products.filter((product) => {
        const skus = skusByProductId.get(product.id) ?? []
        return [product.name, ...skus.flatMap((s) => [s.name, s.code])].some((v) => normalize(v).includes(q))
      })
    }
    const activeId = categoryPath[categoryPath.length - 1]
    if (!activeId) return products
    const active = findCategoryById(categories, activeId)
    const ids = new Set([activeId, ...(active ? getDescendants(active) : [])])
    return products.filter((p) => p.mainCategoryId && ids.has(p.mainCategoryId))
  }, [products, skusByProductId, query, categoryPath, categories])

  const quantityInTicket = (skuId: string) =>
    items.filter((item) => item.skuId === skuId).reduce((sum, item) => sum + item.amount, 0)

  const productQuantityInTicket = (productId: string) =>
    items.filter((item) => item.productId === productId).reduce((sum, item) => sum + item.amount, 0)

  const addSkuToItems = (sku: Sku, productName: string) => {
    if (getAvailableToAdd(sku.id) <= 0) {
      return
    }

    const existingItemIndex = items.findIndex((item) => item.skuId === sku.id)

    if (existingItemIndex >= 0) {
      const existingItem = items[existingItemIndex]
      const maxQuantity = getMaxQuantityForLine(sku.id, existingItemIndex)
      const nextAmount = Math.min(existingItem.amount + 1, maxQuantity)
      if (nextAmount <= existingItem.amount) {
        return
      }
      const updatedItems = [...items]
      updatedItems[existingItemIndex] = existingItem.setAmount(nextAmount)
      onItemsChange(updatedItems)
    } else {
      const newItem = new Item(
        1,
        skuUnitPrice(sku),
        skuTaxRate(sku),
        sku.productId,
        productName,
        undefined,
        undefined,
        sku.id,
        sku.name,
      )
      onItemsChange([...items, newItem])
    }
  }

  const handleProductClick = (product: Product) => {
    const productSkus = skusByProductId.get(product.id) ?? []

    const hasAxes = (product.variantAxes?.length ?? 0) > 0

    if (hasAxes || productSkus.length > 1) {
      setSelectedProductId(product.id)
      return
    }

    if (productSkus.length === 1) {
      addSkuToItems(productSkus[0], product.name)
      return
    }

    // produkt bez SKU: řádek jen s produktem a nulovou cenou
    const existingItemIndex = items.findIndex((item) => item.productId === product.id && !item.skuId)

    if (existingItemIndex >= 0) {
      const existingItem = items[existingItemIndex]
      const updatedItems = [...items]
      updatedItems[existingItemIndex] = existingItem.setAmount(existingItem.amount + 1)
      onItemsChange(updatedItems)
    } else {
      onItemsChange([...items, new Item(1, 0, undefined, product.id, product.name)])
    }
  }

  const productAvailability = (skus: Sku[]) => {
    if (skus.length === 0) return Number.POSITIVE_INFINITY
    return Math.max(...skus.map((sku) => getAvailableToAdd(sku.id)))
  }

  if (selectedProduct) {
    const skus = skusByProductId.get(selectedProduct.id) ?? []
    const onSkuClick = (sku: Sku) => {
      addSkuToItems(sku, selectedProduct.name)
      // po výběru varianty zpět na mřížku bez dalšího kroku v historii
      setSelectedProductId(null, { history: 'replace' })
    }

    return (selectedProduct.variantAxes?.length ?? 0) > 0 ? (
      <VariantAxisPicker
        product={selectedProduct}
        skus={skus}
        onSkuResolved={onSkuClick}
        onBack={() => setSelectedProductId(null)}
        getAvailableToAdd={getAvailableToAdd}
      />
    ) : (
      <SkuSelectionView
        productName={selectedProduct.name}
        skus={skus}
        onSkuClick={onSkuClick}
        onBack={() => setSelectedProductId(null)}
        getAvailableToAdd={getAvailableToAdd}
        quantityInTicket={quantityInTicket}
      />
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 space-y-2 pb-3 md:space-y-3 md:pb-4">
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value || null)}
            placeholder="Hledat produkt, variantu nebo kód"
            className="h-11 rounded-lg pr-12 pl-10 text-base md:h-12 md:text-[15px]"
            aria-label="Hledat produkt"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery(null)}
              aria-label="Smazat hledání"
              className="absolute top-0 right-0 inline-flex size-12 items-center justify-center text-muted-foreground hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        {!query && (
          <CategoryNavigation
            categories={categories}
            path={categoryPath}
            onPathChange={(path) => setCategoryPath(path.length ? path : null)}
            isVisible={isVisible}
          />
        )}
      </div>

      {/* p-1 a -m-1 nechávají místo pro focus ring dlaždic u okraje posuvné plochy */}
      <ScrollFadeArea className="-m-1 p-1">
        {productsLoading && (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3">
            {Array.from({ length: 9 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        )}

        {!productsLoading && visibleProducts.length > 0 && (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3">
            {visibleProducts.map((product) => {
              const skus = skusByProductId.get(product.id) ?? []
              return (
                <ProductTile
                  key={product.id}
                  name={product.name}
                  meta={skus.length > 1 ? `${skus.length} ${plural(skus.length, 'varianta', 'varianty', 'variant')}` : skus[0] && !skus[0].isSystem ? skus[0].code : undefined}
                  price={priceDisplay(skus)}
                  available={productAvailability(skus)}
                  inTicket={productQuantityInTicket(product.id)}
                  onClick={() => handleProductClick(product)}
                />
              )
            })}
          </div>
        )}

        {!productsLoading && visibleProducts.length === 0 && (
          <div className="grid place-items-center gap-1 rounded-xl bg-muted/70 px-6 py-12 text-center">
            <p className="text-sm font-medium">{query ? 'Nic jsme nenašli' : 'V katalogu zatím nejsou produkty'}</p>
            <p className="text-sm text-muted-foreground">
              {query ? 'Zkuste jiný název nebo kód, případně přidejte vlastní položku.' : 'Prodávejte přes vlastní položku, nebo přidejte produkty v katalogu.'}
            </p>
          </div>
        )}
      </ScrollFadeArea>
    </div>
  )
}
